// src/utils/analytics.js
import ReactGA from 'react-ga4';

const initializeAnalytics = () => {
  ReactGA.initialize('G-H3YQE71L9S', {
    gtagOptions: {
      send_page_view: false // We'll send page views manually on route changes
    }
  });
};

const logPageView = (path) => {
  // Send pageview with proper GA4 format
  ReactGA.send({
    hitType: "pageview",
    page: path,
    title: document.title
  });

  // Also log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 GA Pageview:', path);
  }
};

/**
 * Track key events (formerly conversions) for important user actions
 * @param {string} eventName - The event name
 * @param {Object} params - Event parameters
 */
const trackEvent = (eventName, params = {}) => {
  ReactGA.event(eventName, params);

  if (process.env.NODE_ENV === 'development') {
    console.log('📊 GA Event:', eventName, params);
  }
};

/**
 * Track when a decal is rendered on the 3D model - the "aha moment"
 * This is a key event that correlates strongly with user activation
 * @param {Object} params - Event parameters
 * @param {string} params.source - 'upload' | 'ai_generation' | 'text'
 * @param {string} params.bodyPart - The body model name (e.g., 'Arm', 'Male Torso')
 * @param {number} params.decalCount - Total number of decals after this addition
 * @param {boolean} params.isPremiumUser - Whether user has premium subscription
 */
const trackDecalRendered = ({ source, bodyPart, decalCount, isPremiumUser }) => {
  const params = {
    design_source: source || 'unknown',
    body_part: bodyPart || 'unknown',
    decal_count: decalCount || 1,
    user_type: isPremiumUser ? 'premium' : 'free'
  };

  trackEvent('decal_rendered', params);

  // Also fire as a gtag event for conversion tracking in GA4
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'decal_rendered', params);
  }
};

/**
 * Track AI generation events
 * @param {string} action - 'started' | 'completed' | 'failed' | 'variation_selected'
 * @param {Object} params - Additional parameters
 */
const trackAIGeneration = (action, params = {}) => {
  trackEvent('ai_generation', {
    action,
    ...params
  });
};

/**
 * Track upgrade prompt interactions
 * @param {string} action - 'shown' | 'clicked' | 'dismissed'
 * @param {string} trigger - What triggered the prompt (e.g., 'design_limit', 'premium_model')
 */
const trackUpgradePrompt = (action, trigger) => {
  trackEvent('upgrade_prompt', {
    action,
    trigger
  });
};

/**
 * Track when a design is saved
 * @param {Object} params - Event parameters
 * @param {number} params.decalCount - Number of designs saved
 * @param {string} params.bodyPart - Body model used
 * @param {boolean} params.isPremiumUser - User subscription status
 */
const trackDesignSaved = ({ decalCount, bodyPart, isPremiumUser }) => {
  trackEvent('design_saved', {
    decal_count: decalCount || 1,
    body_part: bodyPart || 'unknown',
    user_type: isPremiumUser ? 'premium' : 'free'
  });
};

/**
 * Track social share clicks
 * @param {string} platform - 'facebook' | 'twitter' | 'whatsapp' | 'copy_link'
 */
const trackShareClicked = (platform) => {
  trackEvent('share_clicked', {
    platform
  });
};

/**
 * Track design source selection - how users create their designs
 * @param {string} method - 'upload' | 'text' | 'ai_generation'
 * @param {Object} params - Additional parameters
 */
const trackDesignSource = (method, params = {}) => {
  trackEvent('design_source_selected', {
    method,
    ...params
  });
};

const ANON_ID_KEY = 'anon_visitor_id';

/**
 * Get (or lazily create) a persistent anonymous visitor id.
 * Stored in localStorage so repeat visits from the same browser are counted
 * as one anonymous user server-side. Returns null if storage is unavailable.
 */
const getAnonVisitorId = () => {
  try {
    let id = window.localStorage.getItem(ANON_ID_KEY);
    if (!id) {
      id = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `anon_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      window.localStorage.setItem(ANON_ID_KEY, id);
    }
    return id;
  } catch (e) {
    return null;
  }
};

/**
 * Record that a signed-out visitor created a design (the "aha moment" while
 * not logged in). Fires the standard GA event AND pings the backend so the
 * admin dashboard can report unique anonymous creators. Fire-and-forget:
 * failures never disrupt the design flow.
 * @param {string} source - 'upload' | 'text' | 'ai_generation'
 */
const trackAnonDesignCreated = (source) => {
  // GA4 event for funnel analysis
  trackEvent('anon_design_created', { design_source: source || 'unknown' });

  const anonId = getAnonVisitorId();
  if (!anonId) return;

  const apiUrl = process.env.REACT_APP_API_URL || 'https://koi-2028.onrender.com';
  const payload = JSON.stringify({ anon_id: anonId, source: source || 'unknown' });

  try {
    // Prefer sendBeacon so it survives navigation/tab-close.
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${apiUrl}/track/anon-design`,
        new Blob([payload], { type: 'application/json' })
      );
    } else {
      fetch(`${apiUrl}/track/anon-design`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(() => {});
    }
  } catch (e) {
    // Never let tracking break the UX.
  }
};

export {
  initializeAnalytics,
  logPageView,
  trackEvent,
  trackDecalRendered,
  trackAIGeneration,
  trackUpgradePrompt,
  trackDesignSaved,
  trackShareClicked,
  trackDesignSource,
  trackAnonDesignCreated
};